import { Contact, Customer } from '../types/crm';
import { normalizePhoneNumber } from './phoneFormatting';

/** Polish and Central European letters that are not decomposed by Unicode NFD. */
const STANDALONE_DIACRITIC_MAP: Record<string, string> = {
  Ł: 'L',
  ł: 'l',
  Ą: 'A',
  ą: 'a',
  Ć: 'C',
  ć: 'c',
  Ę: 'E',
  ę: 'e',
  Ń: 'N',
  ń: 'n',
  Ó: 'O',
  ó: 'o',
  Ś: 'S',
  ś: 's',
  Ź: 'Z',
  ź: 'z',
  Ż: 'Z',
  ż: 'z',
};

/** Remove accents/diacritics so search matches consistently (e.g. Mariño → Marino, Rafał → Rafal). */
export function stripDiacritics(value: string): string {
  const decomposed = value.normalize('NFD').replace(/\p{M}/gu, '');
  return decomposed.replace(/[ŁłĄąĆćĘęŃńÓóŚśŹźŻż]/g, (ch) => STANDALONE_DIACRITIC_MAP[ch] ?? ch);
}

/** Capitalise the first letter of each word; remaining letters are lowercased. */
export function formatTitleCase(value: string): string {
  return stripDiacritics(value)
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
}

function formatOptionalTitleCase(value: string | null | undefined): string | null | undefined {
  if (value == null) return value;
  const trimmed = stripDiacritics(value).trim();
  if (!trimmed) return trimmed;
  return formatTitleCase(trimmed);
}

/** Normalise pasted participant first/last names before matching or persisting. */
export function normalizeParticipantNameParts(
  firstName: string,
  surname: string
): { firstName: string; surname: string } {
  return {
    firstName: firstName.trim() ? formatTitleCase(firstName.trim()) : '',
    surname: surname.trim() ? formatTitleCase(surname.trim()) : '',
  };
}

/** Normalise person/company names and phone numbers before persisting CRM records. */
export function normalizeCustomerNameFields(customer: Customer): Customer {
  return {
    ...customer,
    firstName: formatOptionalTitleCase(customer.firstName) ?? undefined,
    surname: formatOptionalTitleCase(customer.surname) ?? undefined,
    companyName: formatOptionalTitleCase(customer.companyName) ?? '',
    contactPhoneNumber: normalizePhoneNumber(customer.contactPhoneNumber),
    locations: customer.locations?.map((location) => ({
      ...location,
      locationContactName: formatOptionalTitleCase(location.locationContactName) ?? undefined,
      locationContactPhone: normalizePhoneNumber(location.locationContactPhone) ?? '',
    })),
    contacts: customer.contacts?.map((contact) => ({
      ...contact,
      firstName: formatTitleCase(contact.firstName),
      lastName: formatTitleCase(contact.lastName),
      phoneNumber: normalizePhoneNumber(contact.phoneNumber),
    })),
  };
}

/** Normalise contact name and phone fields before persisting. */
export function normalizeContactFields(contact: Contact): Contact {
  return {
    ...contact,
    firstName: formatTitleCase(contact.firstName),
    lastName: formatTitleCase(contact.lastName),
    phoneNumber: normalizePhoneNumber(contact.phoneNumber),
  };
}
